import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS, LAYOUT, SPACING } from '../constants/theme';
import { UIButton } from './UIButton';
import type { LayoutMetrics } from '../LayoutManager';

export type LobbyMode = 'create' | 'join' | 'joining';

export interface LobbyOverlayOptions {
  mode: LobbyMode;
  onCancel: () => void;
  onConnect?: (roomCode: string) => void; // Only for join mode (used by join mode callback)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

/**
 * Overlay for creating/joining online multiplayer rooms
 */
export class LobbyOverlay extends Container {
  private options: LobbyOverlayOptions;
  private background: Graphics;
  private panel: Container;
  private panelBg: Graphics;
  private titleText: Text;
  private statusText: Text;
  private roomCodeText: Text | null = null;
  private cancelButton: UIButton;
  private connectButton: UIButton | null = null;

  // DOM elements for room code input (join mode)
  private inputContainer: HTMLDivElement | null = null;
  private inputElement: HTMLInputElement | null = null;

  // Error message
  private errorText: Text | null = null;

  constructor(metrics: LayoutMetrics, options: LobbyOverlayOptions) {
    super();
    this.options = options;

    // Semi-transparent background
    this.background = new Graphics();
    this.background.rect(0, 0, metrics.width, metrics.height);
    this.background.fill({ color: 0x000000, alpha: 0.8 });
    this.background.eventMode = 'static';
    this.addChild(this.background);

    // Panel
    this.panel = new Container();
    this.panel.eventMode = 'static';
    this.addChild(this.panel);

    const panelWidth = Math.min(400, metrics.width - SPACING.LG * 2);
    // 'joining' mode uses same height as 'create' (no input needed)
    const panelHeight = options.mode === 'join' ? 340 : 260;

    this.panelBg = new Graphics();
    this.panelBg.roundRect(0, 0, panelWidth, panelHeight, LAYOUT.BORDER_RADIUS);
    this.panelBg.fill({ color: COLORS.BG_SECONDARY });
    this.panelBg.stroke({ color: COLORS.PLAYER1, width: 2 });
    this.panelBg.eventMode = 'static';
    this.panel.addChild(this.panelBg);

    // Title
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.XL,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const titleMap: Record<LobbyMode, string> = {
      create: 'Create Room',
      join: 'Join Room',
      joining: 'Joining Room',
    };
    this.titleText = new Text({
      text: titleMap[options.mode],
      style: titleStyle,
    });
    this.titleText.anchor.set(0.5, 0);
    this.titleText.x = panelWidth / 2;
    this.titleText.y = SPACING.LG;
    this.panel.addChild(this.titleText);

    // Status text
    const statusStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fill: COLORS.TEXT_SECONDARY,
    });
    const statusMap: Record<LobbyMode, string> = {
      create: 'Creating room...',
      join: 'Enter the room code to join',
      joining: 'Connecting...',
    };
    this.statusText = new Text({
      text: statusMap[options.mode],
      style: statusStyle,
    });
    this.statusText.anchor.set(0.5, 0);
    this.statusText.x = panelWidth / 2;
    this.statusText.y = SPACING.LG + FONTS.SIZES.XL + SPACING.MD;
    this.panel.addChild(this.statusText);

    if (options.mode === 'create') {
      // Room code display (large)
      const codeStyle = new TextStyle({
        fontFamily: 'monospace',
        fontSize: FONTS.SIZES.TITLE,
        fontWeight: 'bold',
        fill: COLORS.SUCCESS,
        letterSpacing: 8,
      });
      this.roomCodeText = new Text({
        text: '------',
        style: codeStyle,
      });
      this.roomCodeText.anchor.set(0.5);
      this.roomCodeText.x = panelWidth / 2;
      this.roomCodeText.y = panelHeight / 2;
      this.panel.addChild(this.roomCodeText);
    } else if (options.mode === 'joining') {
      // 'joining' mode - just show a connecting message, no input needed
      // The status text already shows "Connecting..."
    } else {
      // 'join' mode - full input UI
      // Join mode - create DOM input
      this.createDOMInput(panelWidth, panelHeight);

      // Connect button
      this.connectButton = new UIButton({
        text: 'Connect',
        width: 120,
        variant: 'primary',
        onClick: () => this.handleConnect(),
      });
      this.connectButton.x = panelWidth / 2;
      this.connectButton.y = panelHeight - SPACING.LG - LAYOUT.BUTTON_HEIGHT - SPACING.MD - LAYOUT.BUTTON_HEIGHT;
      this.panel.addChild(this.connectButton);

      // Error text (hidden by default)
      const errorStyle = new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.SM,
        fill: COLORS.DANGER,
      });
      this.errorText = new Text({
        text: '',
        style: errorStyle,
      });
      this.errorText.anchor.set(0.5, 0);
      this.errorText.x = panelWidth / 2;
      this.errorText.y = panelHeight * 0.52;
      this.errorText.visible = false;
      this.panel.addChild(this.errorText);
    }

    // Cancel button
    this.cancelButton = new UIButton({
      text: 'Cancel',
      width: 120,
      variant: 'secondary',
      onClick: () => this.handleCancel(),
    });
    this.cancelButton.x = panelWidth / 2;
    this.cancelButton.y = panelHeight - SPACING.LG - LAYOUT.BUTTON_HEIGHT / 2;
    this.panel.addChild(this.cancelButton);

    // Position panel in center
    this.panel.x = (metrics.width - panelWidth) / 2;
    this.panel.y = (metrics.height - panelHeight) / 2;

    // Animate in
    this.alpha = 0;
    gsap.to(this, { alpha: 1, duration: 0.3 });
    this.panel.scale.set(0.9);
    gsap.to(this.panel.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });
  }

  private createDOMInput(panelWidth: number, panelHeight: number): void {
    // Create DOM container for input
    this.inputContainer = document.createElement('div');
    this.inputContainer.style.cssText = `
      position: fixed;
      z-index: 1000;
      display: flex;
      justify-content: center;
    `;

    // Create input element
    this.inputElement = document.createElement('input');
    this.inputElement.type = 'text';
    this.inputElement.placeholder = 'ABCD12';
    this.inputElement.maxLength = 6;
    this.inputElement.style.cssText = `
      width: 200px;
      height: 48px;
      font-family: monospace;
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 8px;
      background: ${this.colorToHex(COLORS.BG_TERTIARY)};
      border: 2px solid ${this.colorToHex(COLORS.PLAYER1)};
      border-radius: ${LAYOUT.BORDER_RADIUS}px;
      color: white;
      outline: none;
    `;

    // Auto-uppercase input
    this.inputElement.addEventListener('input', () => {
      if (this.inputElement) {
        this.inputElement.value = this.inputElement.value.toUpperCase();
      }
    });

    // Enter key to connect
    this.inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleConnect();
      }
    });

    this.inputContainer.appendChild(this.inputElement);
    document.body.appendChild(this.inputContainer);

    // Position input after a short delay to ensure panel animation is complete
    requestAnimationFrame(() => {
      this.updateInputPosition(panelWidth, panelHeight);
    });
  }

  private updateInputPosition(panelWidth: number, panelHeight: number): void {
    if (!this.inputContainer) return;

    // Get the canvas element to determine its position
    const canvas = document.querySelector('canvas');
    const canvasRect = canvas?.getBoundingClientRect() || { left: 0, top: 0 };

    // Calculate panel position (panel.x and panel.y are set in constructor)
    const panelX = this.panel.x;
    const panelY = this.panel.y;

    // Position input centered in panel, above center to avoid button overlap
    const inputX = canvasRect.left + panelX;
    const inputY = canvasRect.top + panelY + panelHeight * 0.35;

    this.inputContainer.style.left = `${inputX}px`;
    this.inputContainer.style.top = `${inputY}px`;
    this.inputContainer.style.width = `${panelWidth}px`;
  }

  private colorToHex(color: number): string {
    return '#' + color.toString(16).padStart(6, '0');
  }

  private handleConnect(): void {
    if (!this.inputElement) return;

    const roomCode = this.inputElement.value.trim().toUpperCase();
    if (roomCode.length < 4) {
      this.showError('Room code must be at least 4 characters');
      return;
    }

    this.hideError();
    this.setConnecting(true);
    this.options.onConnect?.(roomCode);
  }

  private handleCancel(): void {
    this.hide();
    this.options.onCancel();
  }

  /**
   * Set the room code (for create mode)
   */
  setRoomCode(code: string): void {
    if (this.roomCodeText) {
      this.roomCodeText.text = code;
    }
    this.statusText.text = 'Share this code with another player';
  }

  /**
   * Set connecting state
   */
  setConnecting(connecting: boolean): void {
    if (this.options.mode === 'create') {
      this.statusText.text = connecting
        ? 'Waiting for player to join...'
        : 'Share this code with another player';
    } else if (this.options.mode === 'joining') {
      // 'joining' mode - just update status text
      this.statusText.text = connecting ? 'Connecting...' : 'Connection failed';
    } else {
      // 'join' mode - full input UI
      this.statusText.text = connecting
        ? 'Connecting...'
        : 'Enter the room code to join';

      if (this.connectButton) {
        this.connectButton.setDisabled(connecting);
        this.connectButton.setText(connecting ? 'Connecting...' : 'Connect');
      }

      if (this.inputElement) {
        this.inputElement.disabled = connecting;
      }
    }

    // Disable cancel button while connecting in join/joining modes
    if (this.options.mode === 'join' || this.options.mode === 'joining') {
      this.cancelButton.setDisabled(connecting);
    }
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    if (this.errorText) {
      this.errorText.text = message;
      this.errorText.visible = true;
    }
    this.setConnecting(false);
  }

  /**
   * Hide error message
   */
  hideError(): void {
    if (this.errorText) {
      this.errorText.visible = false;
    }
  }

  /**
   * Hide and destroy the overlay
   */
  hide(): void {
    // Clean up DOM elements
    if (this.inputContainer) {
      this.inputContainer.remove();
      this.inputContainer = null;
      this.inputElement = null;
    }

    // Animate out
    gsap.to(this, {
      alpha: 0,
      duration: 0.2,
      onComplete: () => {
        this.destroy();
      },
    });
  }

  /**
   * Update layout on resize
   */
  onResize(metrics: LayoutMetrics): void {
    // Update background
    this.background.clear();
    this.background.rect(0, 0, metrics.width, metrics.height);
    this.background.fill({ color: 0x000000, alpha: 0.8 });

    // Recalculate panel position ('joining' mode uses same height as 'create')
    const panelWidth = Math.min(400, metrics.width - SPACING.LG * 2);
    const panelHeight = this.options.mode === 'join' ? 340 : 260;
    this.panel.x = (metrics.width - panelWidth) / 2;
    this.panel.y = (metrics.height - panelHeight) / 2;

    // Update input position
    if (this.inputContainer) {
      this.updateInputPosition(panelWidth, panelHeight);
    }
  }

  /**
   * Clean up when destroyed
   */
  destroy(): void {
    if (this.inputContainer) {
      this.inputContainer.remove();
      this.inputContainer = null;
      this.inputElement = null;
    }
    super.destroy();
  }
}
